import React, { useCallback } from 'react';
import { Badge, Button, Separator } from '@monksflow/monks-ui';
import { ChevronDownIcon, XIcon } from 'lucide-react';

import DropdownSelect from '@/shared/ui/DropdownSelect/DropdownSelect';
import type {
  OpportunitiesFilterProps,
  ActiveFiltersProps,
  FilterSelectionProps,
} from './OpportunitiesFilter.types';
import { useTranslation } from 'react-i18next';

export type { OpportunitiesFilterProps };

const noop = () => {};

const ActiveFilters = ({
  selectedTargetAudiences,
  hasActiveFilters,
  handleRemoveAudience,
}: ActiveFiltersProps): React.JSX.Element | null => {
  const { t } = useTranslation('analysis');

  if (!hasActiveFilters) return null;

  return (
    <div className="flex flex-row flex-wrap justify-end gap-2">
      {selectedTargetAudiences.map((audience) => (
        <Badge key={`audience-${audience}`} variant="secondary" className="gap-1 rounded-full pr-1">
          {audience}
          <button
            type="button"
            aria-label={t('socialMediaOpportunitiesWidget.opportunitiesFilter.removeAudience', {
              audience,
            })}
            onClick={() => handleRemoveAudience(audience)}
            className="flex items-center"
          >
            <XIcon className="size-3" />
          </button>
        </Badge>
      ))}
    </div>
  );
};

const FilterSelection = ({
  filterOptions,
  hasActiveFilters,
  selectedTargetAudiences,
  onTargetAudiencesChange,
  onResetFilters,
  disabled = false,
}: FilterSelectionProps): React.JSX.Element => {
  const { t } = useTranslation('analysis');

  return (
    <div className="flex justify-end gap-2">
      {hasActiveFilters && !disabled && (
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={onResetFilters}
            className="min-w-22.5 text-nowrap"
          >
            {t('socialMediaOpportunitiesWidget.opportunitiesFilter.clearFilters')}
          </Button>
          <Separator orientation="vertical" className="my-auto data-[orientation=vertical]:h-8" />
        </div>
      )}
      <DropdownSelect<string>
        description={t('socialMediaOpportunitiesWidget.opportunitiesFilter.filterByTargetAudience')}
        label={t('socialMediaOpportunitiesWidget.opportunitiesFilter.audience')}
        options={filterOptions.targetAudiences}
        initialValue={[]}
        value={selectedTargetAudiences}
        onChange={onTargetAudiencesChange}
        isMultiSelect
        menuPosition="below"
        keepLabelStatic
        endIcon={<ChevronDownIcon />}
        disabled={disabled}
      />
    </div>
  );
};

export const OpportunitiesFilter = ({
  filterOptions,
  selectedTargetAudiences = [],
  hasActiveFilters = false,
  favoritesOnly,
  onTargetAudiencesChange = noop,
  onResetFilters = noop,
  onFavoritesFilterToggle = noop,
  disabled = false,
}: OpportunitiesFilterProps): React.JSX.Element => {
  const { t } = useTranslation('analysis');

  const handleRemoveAudience = useCallback(
    (audienceToRemove: string) => {
      onTargetAudiencesChange(selectedTargetAudiences.filter((a) => a !== audienceToRemove));
    },
    [onTargetAudiencesChange, selectedTargetAudiences],
  );

  return (
    <>
      <div className="flex w-full flex-col gap-6">
        <p className="w-full text-heading-xl text-foreground">
          {t('socialMediaOpportunitiesWidget.opportunitiesFilter.trendingOpportunities')}
        </p>
        <div className="flex w-full flex-wrap items-center gap-3">
          <div className="flex gap-2">
            <Button
              variant={favoritesOnly ? 'outline' : 'default'}
              aria-pressed={!favoritesOnly}
              onClick={() => onFavoritesFilterToggle(false)}
              data-testid="filter-all"
            >
              {t('socialMediaOpportunitiesWidget.opportunitiesFilter.all')}
            </Button>
            <Button
              variant={favoritesOnly ? 'default' : 'outline'}
              aria-pressed={favoritesOnly}
              onClick={() => onFavoritesFilterToggle(true)}
              data-testid="filter-favorites"
            >
              {t('socialMediaOpportunitiesWidget.opportunitiesFilter.favorites')}
            </Button>
          </div>
          <div className="flex flex-1 flex-wrap items-center justify-end gap-2">
            {!disabled && (
              <ActiveFilters
                selectedTargetAudiences={selectedTargetAudiences}
                hasActiveFilters={hasActiveFilters}
                handleRemoveAudience={handleRemoveAudience}
              />
            )}
            <FilterSelection
              filterOptions={filterOptions}
              selectedTargetAudiences={selectedTargetAudiences}
              onTargetAudiencesChange={onTargetAudiencesChange}
              onResetFilters={onResetFilters}
              hasActiveFilters={hasActiveFilters}
              disabled={disabled}
            />
          </div>
        </div>
      </div>
      <Separator className="bg-border-light" />
    </>
  );
};
